import { useCallback, useEffect, useRef, useState } from "react";

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/**
 * Custom hook for counting up when an element scrolls into view.
 *
 * @param {React.RefObject} ref - The element reference to observe.
 * @param {number} end - The final count value.
 * @param {number} duration - Animation duration in ms (default: 2000).
 * @returns {number} The current count.
 */
const useScrollTriggeredCountUp = (ref, end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const isCounting = useRef(false);
  const startTime = useRef(null);

  const animateCount = useCallback(
    (timestamp) => {
      if (!startTime.current) startTime.current = timestamp;
      const elapsed = timestamp - startTime.current;
      const progress = Math.min(elapsed / duration, 1);

      setCount(Math.round(end * easeOutExpo(progress)));

      if (progress < 1) {
        requestAnimationFrame(animateCount);
      } else {
        isCounting.current = false;
        startTime.current = null;
      }
    },
    [end, duration]
  );

  const handleIntersection = useCallback(
    ([entry]) => {
      if (entry.isIntersecting && !isCounting.current) {
        isCounting.current = true;
        requestAnimationFrame(animateCount);
      } else if (!entry.isIntersecting) {
        // Reset only if you want it to restart when scrolled back
        setCount(0);
      }
    },
    [animateCount]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.7,
    });

    const el = ref.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [handleIntersection, ref]);

  return count;
};

export default useScrollTriggeredCountUp;
